import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { EquipmentBrowserSection, SummaryConfig } from '@repo/sdk/models/components';

import { useSelectEquipment } from '@/features/equipment-browser/equipment-details/hooks/use-select-equipment';
import { identifiersAtom } from '@/features/equipment-browser/equipment-details/state/selected-equipment.state';
import {
  useGetEquipmentBrowserConfig,
  useGetEquipmentBrowserView,
} from '@/hooks/query-hooks/gaia-api/equipment-browser';
import { useCurrentFacility } from '@/hooks/use-current-facility';

const SelectedEquipmentState = {
  identifiersAtom,
};

export interface JumpToEquipmentReturn {
  sections: EquipmentBrowserSection[] | undefined;
  getEquipmentBySection: (section: EquipmentBrowserSection) => SummaryConfig[] | undefined;
  handleEquipmentClick: (equipment: SummaryConfig, section: EquipmentBrowserSection) => void;
}

export const useJumpToEquipment = (): JumpToEquipmentReturn => {
  const { agentId } = useCurrentFacility();
  const { selectEquipmentAction } = useSelectEquipment();
  const identifiers = useAtomValue(SelectedEquipmentState.identifiersAtom);
  const { data: equipmentBrowserConfig } = useGetEquipmentBrowserConfig({ agentId });
  const { data: equipmentBrowserViewResponse } = useGetEquipmentBrowserView({
    agentId,
    viewId: identifiers?.viewId ?? equipmentBrowserConfig?.defaultView,
  });

  const sections = equipmentBrowserViewResponse?.view?.sections;
  const equipmentList = equipmentBrowserViewResponse?.summaryConfigs;

  const getEquipmentBySection = useCallback(
    (section: EquipmentBrowserSection) => {
      return equipmentList
        ?.filter((equipment) => section.order.includes(equipment.referenceId))
        .sort(
          (a, b) => section.order.indexOf(a.referenceId) - section.order.indexOf(b.referenceId)
        );
    },
    [equipmentList]
  );

  const handleEquipmentClick = (equipment: SummaryConfig, section: EquipmentBrowserSection) => {
    selectEquipmentAction(equipment.referenceId, section.id);
  };

  return {
    sections,
    getEquipmentBySection,
    handleEquipmentClick,
  };
};
